import {useEffect, useRef} from 'react';
import store from "../../redux/store";
import {BOT_ACTION_INTERVAL_MS} from "../../utils/bots/botConfig";
import {createBotEffectsStore} from "../../utils/bots/botEffects";
import {selectPhase} from "../../redux/reducers/sharedStateReducer/selectors";
import {PHASES} from "../../constants/phases";
import {useReplayControls} from "../../contexts/ReplayControlsContext";
import {tutorialReset} from "../../redux/actions";
import {FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER} from "../../configs/playbackSpeed";
import {
  getAwaitedAnimationsDeadline,
  getLastAwaitedAnimationResultShownAt,
} from "../../utils/replay/replayAwaitedAnimations";
import {
  DEFAULT_PLAYBACK_SPEED_MULTIPLIER,
  getReplayPlaybackSpeedMultiplier,
  resolvePlaybackSpeedMultiplier,
  setReplayPlaybackSpeedMultiplier,
  setReplayRewinding,
} from "../../utils/replay/replayPlaybackSpeed";

// Фазы, которые в записи в основном состоят из служебных действий
// (расстановка стартовых кварталов/подтверждения SETUP, начисление
// снабжения в SUPPLY) — их незачем просматривать в темпе ходов ботов,
// поэтому между ними пауза убирается полностью (независимо от speedMultiplier)
const INSTANT_REPLAY_PHASES = new Set([PHASES.SETUP, PHASES.SUPPLY]);

// Действия, перед которыми пауза не нужна вовсе: смотреть в них нечего.
// SET_TURN_END_TIMESTAMP — служебная отметка дедлайна хода, на поле не видна
// (как и SET_CITY_CAPTURE_TIME_WINDOW — окно облета захваченных городов);
// APPLY_MOVEMENT_ACTIONS и APPLY_RECRUITMENT_ACTIONS — фиксация уже показанных
// перемещений и наймов пачкой, отдельной паузы перед ними зритель не понимает
const INSTANT_REPLAY_ACTION_TYPES = new Set([
  'SET_TURN_END_TIMESTAMP',
  'SET_CITY_CAPTURE_TIME_WINDOW',
  'APPLY_MOVEMENT_ACTIONS',
  'APPLY_RECRUITMENT_ACTIONS',
]);

// SET_GAME_TYPE из bootstrap-префикса никогда не переигрывается: в записи
// сохранен ONLINE, а тип игры уже выставлен в REPLAY снаружи (App.js) —
// см. тот же пропуск в bootstrap-эффекте GameContent.js
const BOOTSTRAP_TYPES_TO_SKIP = new Set(['SET_GAME_TYPE']);

/**
 * Воспроизведение записи завершенного матча: диспатчит по одному сохраненному
 * действию каждые actionIntervalMs (по умолчанию — стандартный интервал ходов
 * ботов, домноженный на speedMultiplier из ReplayControlsContext), в исходном
 * порядке. В отличие от BotRunner ничего не вычисляет — решение уже "запечено"
 * в самом действии, задача рантайма только в паузах между ними. Пока текущая
 * фаза — SETUP или SUPPLY, пауза равна 0 (см. INSTANT_REPLAY_PHASES).
 *
 * Диспатч идет через createBotEffectsStore — ту же обертку, которой озвучены
 * действия ботов в локальных играх: каждое действие получает свой звук/эффект
 * (playEffectForServerAction), как если бы оно пришло от сервера в живой партии.
 *
 * АНИМАЦИИ ПОЛЯ ДОИГРЫВАЮТ: удары ближнего боя, залпы выстрелов, переход
 * отряда (см. playMeleeAnimation, playShotAnimation, playUnitMovementAnimation).
 * Такая анимация бывает длиннее шага воспроизведения, а следующим действием
 * может оказаться завершение этапа — после него погибшие отряды сняты с поля,
 * и анимация оборвалась бы на полпути. Поэтому, пока она на поле, очередное
 * действие не исполняется: тик ждет ее конца (см. checkIfFieldAnimationAwaited),
 * а у ударов и залпов еще и дает итогу побыть на поле. Быстрее x5 анимации
 * не запускаются, и ждать нечего; если зритель поднял скорость выше x5
 * посреди анимации, запись тоже идет дальше, не дожидаясь ее. Ручной шаг
 * вперед не ждет — это прямая команда зрителя.
 *
 * Управление (пауза/шаг/скорость) приходит из ReplayControlsContext, которым
 * также пользуется ReplayPlaybackPanel — так кнопки панели и цикл
 * воспроизведения работают с одним и тем же состоянием, не будучи связаны
 * напрямую пропсами.
 */
// Запись прохождения обучения проигрывается в темпе самого игрока (паузы по
// отметкам времени записи), но не дольше этого: минута раздумий игрока над
// модалкой зрителю не нужна — достаточно заметной паузы
export const RECORDED_TIMING_MAX_DELAY_MS = 3000;
// Как часто тик проверяет, отыграла ли анимация поля (удары, залп, переход отряда)
export const FIELD_ANIMATION_POLL_MS = 50;

// Сколько итог анимации (значки мечей и урон на плашках после ударов, урон и
// линия выстрела после залпа) стоит на поле, прежде чем запись пойдет дальше.
// Обычно его и так держит хвост анимации: всплывающие «−N» гаснут около 0,6 с
// после открытия итога, осколки валуна — чуть дольше. Но удар может потерь
// не дать (крепость, орки), а у залпа стрел после попадания остаются только
// короткие искры: анимация уходит с поля почти сразу, а следующим действием
// бывает применение урона — итог мелькнул бы на десятую долю секунды
export const FIELD_ANIMATION_RESULT_HOLD_MS = 600;

/**
 * Ждать ли с очередным действием записи: на поле еще идут удары ближнего боя,
 * залп выстрела или переход отряда — либо итог ударов или залпа только что
 * появился.
 *  - Анимацию ждем не дольше ее срока (см. getAwaitedAnimationsDeadline):
 *    вкладку могли скрыть посреди анимации — кадры не идут, она стоит, и
 *    запись встала бы вместе с ней.
 *  - Быстрее x5 не ждем вовсе: анимация там не запускается, а оставшуюся от
 *    меньшей скорости (зритель поднял скорость посреди анимации) не
 *    дожидаемся — запись уже перематывают
 */
export const checkIfFieldAnimationAwaited = (now = Date.now()) => {
  if (getReplayPlaybackSpeedMultiplier() > FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER) {
    return false;
  }
  const deadlineAt = getAwaitedAnimationsDeadline();
  if (deadlineAt !== null) {
    return now < deadlineAt;
  }
  const resultShownAt = getLastAwaitedAnimationResultShownAt();
  return resultShownAt !== null && now >= resultShownAt && now - resultShownAt < FIELD_ANIMATION_RESULT_HOLD_MS;
};

// Проигрывать нечего (запись доиграна) или пауза: цикл тиков продолжается —
// чтобы шаг назад или снятие паузы подхватились, — но не вхолостую каждые 0 мс
export const RECORDED_TIMING_IDLE_POLL_MS = 250;

/**
 * Пауза перед действием записи, сделанной с отметками времени (recordedAt):
 * как было у игрока, в пределах RECORDED_TIMING_MAX_DELAY_MS, с учетом скорости
 */
export const getRecordedTimingDelayMs = (previousAction, nextAction, speedMultiplier = 1) => {
  const previousAt = previousAction?.recordedAt;
  const nextAt = nextAction?.recordedAt;
  if (!Number.isFinite(previousAt) || !Number.isFinite(nextAt)) {
    return 0;
  }
  const delayMs = Math.min(Math.max(nextAt - previousAt, 0), RECORDED_TIMING_MAX_DELAY_MS);
  return delayMs / (speedMultiplier || 1);
};

const ReplayRunner = ({
                        actions,
                        bootstrapActions,
                        actionIntervalMs = BOT_ACTION_INTERVAL_MS,
                        onFinished,
                        // Паузы по отметкам времени записи (запись обучения), а не
                        // фиксированным интервалом ходов ботов
                        isRecordedTiming = false,
                        // Скорость просмотра, которую видит зритель, когда она
                        // задана не слайдером записи: витрина главного меню
                        // ускоряется своим шагом и передает число со своего
                        // ползунка. По ней сцена решает, показывать ли анимации
                        playbackSpeedMultiplier,
                      }) => {
  const dispatchedCountRef = useRef(0);
  // Запись доиграна до конца — сообщаем об этом один раз. Нужно витрине
  // главного меню: у брошенных матчей записи кончаются без итогов партии, и
  // без этого сигнала витрина замерла бы на последнем кадре навсегда
  const finishedNotifiedRef = useRef(false);
  const onFinishedRef = useRef(onFinished);
  useEffect(() => {
    onFinishedRef.current = onFinished;
  }, [onFinished]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const controllerRef = useRef(null);

  const {
    isPaused,
    speedMultiplier,
    stepRequest,
    clearStepRequest,
    setIsRewinding,
  } = useReplayControls();

  // isPaused/speedMultiplier читаются тиком через ref, а не через замыкание
  // над пропом/состоянием — иначе пришлось бы пересоздавать весь эффект
  // (и обнулять dispatchedCountRef) при каждом клике паузы или движении слайдера
  const isPausedRef = useRef(isPaused);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const speedMultiplierRef = useRef(speedMultiplier);
  useEffect(() => {
    speedMultiplierRef.current = speedMultiplier;
  }, [speedMultiplier]);

  // Шаг воспроизведения тоже читается через ref: витрина главного меню меняет
  // его слайдером скорости, а перезапуск эффекта обнулил бы счетчик
  // проигранных действий — запись начиналась бы с начала на каждое движение
  // слайдера
  const actionIntervalMsRef = useRef(actionIntervalMs);
  useEffect(() => {
    actionIntervalMsRef.current = actionIntervalMs;
  }, [actionIntervalMs]);

  // Скорость просмотра нужна сцене и рендереру, а React-контекст до них не
  // дотягивается: держим ее в модуле (см. utils/replay/replayPlaybackSpeed).
  // Страница записи ускоряется слайдером при стандартном шаге — множитель
  // считается из них; витрина главного меню ускоряется своим шагом и число
  // со своего ползунка передает сама (playbackSpeedMultiplier)
  useEffect(() => {
    setReplayPlaybackSpeedMultiplier(
      playbackSpeedMultiplier ?? resolvePlaybackSpeedMultiplier(actionIntervalMs, speedMultiplier)
    );

    return () => setReplayPlaybackSpeedMultiplier(DEFAULT_PLAYBACK_SPEED_MULTIPLIER);
  }, [actionIntervalMs, speedMultiplier, playbackSpeedMultiplier]);

  useEffect(() => {
    dispatchedCountRef.current = 0;
    cancelledRef.current = false;
    finishedNotifiedRef.current = false;

    if (!actions || actions.length === 0) {
      return;
    }

    const effectsStore = createBotEffectsStore(store);

    const dispatchSafely = (action, position) => {
      try {
        effectsStore.dispatch(action);
      } catch (error) {
        console.error(`[ReplayRunner] Ошибка на действии #${position} (${action?.type})`, error);
      }
    };

    // Диспатч в обход createBotEffectsStore — прямо в "сырой" store. Сброс +
    // перемотка вперед (шаг назад) переигрывает потенциально сотни исторических
    // действий за один синхронный проход; через effectsStore каждое из них
    // тянуло бы за собой playEffectForServerAction (звук/эффект) — залп из
    // десятков звуков разом и заметное подвисание вкладки на самом проходе.
    // Обычный тик воспроизведения/шаг вперед по-прежнему идет через
    // effectsStore — там это ровно одно действие за раз, в естественном темпе
    const dispatchRawSafely = (action, position) => {
      try {
        store.dispatch(action);
      } catch (error) {
        console.error(`[ReplayRunner] Ошибка на действии #${position} при перемотке (${action?.type})`, error);
      }
    };

    // Redux-действия необратимы в общем случае, поэтому "шаг назад" — это не
    // настоящий undo, а полный сброс к состоянию сразу после bootstrap и
    // быстрая перемотка вперед заново до нужного индекса. Это безопасно и
    // детерминированно: LOAD_STATE переписывает hexes/gameState целиком (не
    // мерджит поверх текущего "грязного" стейта — см. sharedStateReducer),
    // а корректный currentPlayerIndex гарантирован тем, что START_GAME несет
    // initiativeShift в самой записи, а не считает его заново
    const resetToBootstrap = () => {
      // Прогресс главы обучения живет в личном состоянии, которое bootstrap
      // не трогает: без сброса после перемотки к началу был бы виден шаг, с
      // которого перематывали. Записи сетевых матчей это не задевает
      store.dispatch(tutorialReset());
      (bootstrapActions || []).forEach((action) => {
        if (!BOOTSTRAP_TYPES_TO_SKIP.has(action.type)) {
          store.dispatch(action);
        }
      });
    };

    // На время перемотки выставляется признак (см. setReplayRewinding):
    // анимация ударов ближнего боя запускается из middleware стора и без него
    // отыграла бы на сырых диспатчах давно прошедшие бои
    const fastForwardTo = (targetCount) => {
      setReplayRewinding(true);
      try {
        resetToBootstrap();
        for (let i = 0; i < targetCount; i += 1) {
          dispatchRawSafely(actions[i], i);
        }
      } finally {
        setReplayRewinding(false);
      }
    };

    const getNextDelayMs = () => {
      // Пауза относится к действию, которое диспатчнет следующий тик, — по
      // нему и решаем, ждать ли перед ним вообще
      const nextAction = actions[dispatchedCountRef.current];
      if (isRecordedTiming) {
        if (!nextAction || isPausedRef.current) {
          return RECORDED_TIMING_IDLE_POLL_MS;
        }
        // Обучение: и фаза расстановки, и снабжение — это уроки, где игроки
        // как раз и бросают главу; проматывать их мгновенно нельзя
        const previousAction = dispatchedCountRef.current > 0
          ? actions[dispatchedCountRef.current - 1]
          : (bootstrapActions || [])[(bootstrapActions || []).length - 1];
        return getRecordedTimingDelayMs(previousAction, nextAction, speedMultiplierRef.current);
      }
      if (nextAction && INSTANT_REPLAY_ACTION_TYPES.has(nextAction.type)) {
        return 0;
      }

      const phase = selectPhase(store.getState());
      if (INSTANT_REPLAY_PHASES.has(phase)) {
        return 0;
      }
      return actionIntervalMsRef.current / speedMultiplierRef.current;
    };

    const scheduleNextTick = () => {
      if (cancelledRef.current) return;
      timerRef.current = setTimeout(tick, getNextDelayMs());
    };

    // Тик планирует себя заново безусловно — даже на паузе (просто не
    // диспатчит) и даже дойдя до конца записи (просто ничего не делает).
    // Если прекращать цепочку по достижении конца, шаг назад после конца
    // записи не смог бы возобновить автовоспроизведение: остановленную
    // цепочку setTimeout нечем перезапустить самостоятельно
    const tick = () => {
      if (cancelledRef.current) return;

      if (!isPausedRef.current && dispatchedCountRef.current < actions.length) {
        // Удары или залп еще идут: действие подождет их конца
        if (checkIfFieldAnimationAwaited()) {
          timerRef.current = setTimeout(tick, FIELD_ANIMATION_POLL_MS);
          return;
        }

        const position = dispatchedCountRef.current;
        dispatchSafely(actions[position], position);
        dispatchedCountRef.current += 1;

        if (dispatchedCountRef.current >= actions.length && !finishedNotifiedRef.current) {
          finishedNotifiedRef.current = true;
          onFinishedRef.current?.();
        }
      }

      scheduleNextTick();
    };

    controllerRef.current = {
      stepForward: () => {
        if (dispatchedCountRef.current >= actions.length) return;
        const position = dispatchedCountRef.current;
        dispatchSafely(actions[position], position);
        dispatchedCountRef.current += 1;
      },
      stepBackward: (count) => {
        const targetCount = Math.max(0, dispatchedCountRef.current - count);
        fastForwardTo(targetCount);
        dispatchedCountRef.current = targetCount;
        // Отмотали назад — запись снова не доиграна, и ее конец опять станет
        // событием
        finishedNotifiedRef.current = false;
      },
      // К самому началу — это просто resetToBootstrap без единого шага
      // перемотки вперед, дешевле обычного stepBackward независимо от того,
      // как далеко продвинулся матч
      jumpToStart: () => {
        resetToBootstrap();
        dispatchedCountRef.current = 0;
        finishedNotifiedRef.current = false;
      },
    };

    scheduleNextTick();

    return () => {
      cancelledRef.current = true;
      controllerRef.current = null;
      clearTimeout(timerRef.current);
    };
    // actionIntervalMs намеренно не в зависимостях — см. actionIntervalMsRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, bootstrapActions, isRecordedTiming]);

  useEffect(() => {
    if (!stepRequest || !controllerRef.current) {
      return;
    }

    if (stepRequest.direction === 'forward') {
      controllerRef.current.stepForward();
      clearStepRequest();
      return;
    }

    if (stepRequest.direction === 'backward' || stepRequest.direction === 'start') {
      // isRewinding выставляется здесь, а тяжелая синхронная перемотка —
      // только после следующей отрисовки. setTimeout(0) для этого ненадежен:
      // он не гарантирует, что браузер успеет закрасить кадр с reload.png до
      // того, как поток заблокируется самой перемоткой (иногда проскакивал
      // без отрисовки вовсе) — двойной requestAnimationFrame ждет именно
      // завершенного кадра
      setIsRewinding(true);
      const {direction, count} = stepRequest;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelledRef.current && controllerRef.current) {
            if (direction === 'start') {
              controllerRef.current.jumpToStart();
            } else {
              controllerRef.current.stepBackward(count);
            }
          }
          setIsRewinding(false);
          clearStepRequest();
        });
      });
    }
  }, [stepRequest, clearStepRequest, setIsRewinding]);

  return null;
};

export default ReplayRunner;
